/**
 * travelValidation — Runtime-Validierung an der Schreibgrenze der Dienstreisen-/
 * Reisekostenerfassung (RK-3, Fehler-Codes RK-18/E17).
 *
 * Kein `zod` im Repo (§3.3-Auftrag) → manuelle Validatoren nach dem etablierten Repo-Muster
 * (`expensesApi.validateCentFields`): reine Funktionen, die ein Result/eine Issue-Liste
 * zurückgeben statt zu werfen. Der Aufrufer (`travelApi.ts`) entscheidet, ob/wann er daraus
 * eine `throw` macht.
 *
 * RK-18/E17 (Breaking Change, modul-intern, architecture-reisekosten.md §14.3/§14.8):
 * Validatoren lieferten bis RK-18 rohe, menschenlesbare Strings (inkl. Paragraphen-Zitaten
 * und Feldpfaden) — diese erreichten unübersetzt/unverändert das UI. Ab RK-18 liefert jeder
 * Validator einen stabilen {@link TravelValidationIssueCode} + ein optionales technisches
 * `detail` (NUR für `logger.error`, NIE für den Render-Pfad — V23). Der EINE UI-Mapper
 * `formatTravelError` (`utils/travel/travelErrorMessages.ts`) übersetzt Codes über i18n-Keys
 * `validation.<code>`. Einzige Consumer dieser Breaking-Change-Signatur sind `travelApi.ts`
 * und die Tests dieses Moduls (Blast-Radius §14.8) — bewusst modul-intern.
 *
 * Diese Validierung ist eine ZUSÄTZLICHE Schicht — sie ersetzt NICHT den Layer-1-Guard IM
 * Berechnungskern (`travelAllowance.ts` Regel 8/9): der Kern verlässt sich NIE auf diese
 * Datei, und diese Datei ersetzt NIE eine eigene Prüfung des Kerns (architecture-reisekosten.md
 * §3.2 Regel 8, V12). Beide Schichten prüfen unabhängig — Defense-in-Depth.
 *
 * @module travelValidation
 */

import type { TravelCalcResult } from './travelAllowance';
import type { TravelDayRecord, TravelMileage } from './travelCalcTypes';
import type { CreateTravelTripInput, TravelTrip, UpdateTravelTripPatch } from '../../types/travelTypes';

// ─── Fehler-Codes (RK-18, architecture-reisekosten.md §14.3) ──────────────────

/**
 * Stabiler, i18n-fähiger Code je Validierungsproblem. 21 Codes gesamt — die aktuell (RK-18)
 * tatsächlich erreichbaren 16 Codes werden von den Validatoren dieser Datei geworfen; die
 * restlichen 5 (`routeNoteRequiredDetour`, `addressEmpty`, `addressTooLong`,
 * `suggestedKmInvalid`, `kmSourceInconsistent`) gehören zum GEMEINSAMEN Fehlercode-Vokabular
 * der Route-Lookup-Erweiterung (RK-19, neue optionale `TravelMileage`-Felder) und werden dort
 * produziert — die Codes sind hier bereits Teil des Typs, damit die i18n-Texte (§14.9) und der
 * UI-Mapper einmalig vollständig stehen (RK-19 muss `travel.json` dafür NICHT erneut anfassen).
 */
export type TravelValidationIssueCode =
  | 'invalidDepartureDate'
  | 'invalidReturnDate'
  | 'returnBeforeDeparture'
  | 'daysCountMismatch'
  | 'daysCoverageMismatch'
  | 'lastDayOvernightMustBeFalse'
  | 'invalidCountryCode'
  | 'emptyRegionKey'
  | 'kmNotFinite'
  | 'kmNotPositive'
  | 'kmTooManyDecimals'
  | 'routeNoteRequired'
  | 'routeNoteRequiredDetour'
  | 'invalidDepartureTime'
  | 'invalidReturnTime'
  | 'purposeRequired'
  | 'destinationCityRequired'
  | 'addressEmpty'
  | 'addressTooLong'
  | 'suggestedKmInvalid'
  | 'kmSourceInconsistent';

/** Ein einzelnes Validierungsproblem: stabiler Code + technisches Detail (NUR für Logs, V23). */
export interface TravelValidationIssue {
  code: TravelValidationIssueCode;
  /** Technisches Detail MIT Werten (z. B. erhaltener Wert, Datum) — NUR für logger, NIE für den Render-Pfad (V23). */
  detail: string;
}

// ─── Result-Typ ───────────────────────────────────────────────────────────────

/** Ergebnis eines Validators: entweder gültig, oder eine nicht-leere Issue-Liste. */
export interface ValidationResult {
  valid: boolean;
  /** Strukturierte Validierungsprobleme (leer, wenn `valid === true`). */
  issues: TravelValidationIssue[];
}

function ok(): ValidationResult {
  return { valid: true, issues: [] };
}

function fail(issues: TravelValidationIssue[]): ValidationResult {
  return { valid: false, issues };
}

// ─── Primitive Format-Validatoren ─────────────────────────────────────────────

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
/** '00:00'–'23:59', 24h — identisches Muster wie der Kern-Parser (travelAllowance.ts). */
const HH_MM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
/** ISO-3166-1 alpha-2, zwei Großbuchstaben (§13.10 Schreibgrenze — heute ungeprüft). */
const COUNTRY_CODE_RE = /^[A-Z]{2}$/;
const MS_PER_DAY = 86_400_000;

/** Prüft, ob `time` ein gültiges 'HH:mm' (24h, 00:00–23:59) ist — z.B. NICHT '24:00'. */
export function isValidHhMm(time: string): boolean {
  return HH_MM_RE.test(time);
}

/** Prüft, ob `dateISO` ein gültiges, existierendes Kalenderdatum 'YYYY-MM-DD' ist. */
export function isValidIsoDate(dateISO: string): boolean {
  if (!ISO_DATE_RE.test(dateISO)) {
    return false;
  }
  const year = Number(dateISO.slice(0, 4));
  const month = Number(dateISO.slice(5, 7));
  const day = Number(dateISO.slice(8, 10));
  const utcMs = Date.UTC(year, month - 1, day);
  if (!Number.isFinite(utcMs)) {
    return false;
  }
  const roundTrip = new Date(utcMs);
  // Verwirft überrollte Daten wie '2026-02-30' (Date.UTC normalisiert sie sonst auf März).
  return (
    roundTrip.getUTCFullYear() === year &&
    roundTrip.getUTCMonth() === month - 1 &&
    roundTrip.getUTCDate() === day
  );
}

/** Tagesnummer seit Epoch (UTC-Mitternacht) für ein bereits validiertes 'YYYY-MM-DD'. */
function isoDateToDayNumber(dateISO: string): number {
  const year = Number(dateISO.slice(0, 4));
  const month = Number(dateISO.slice(5, 7));
  const day = Number(dateISO.slice(8, 10));
  return Math.round(Date.UTC(year, month - 1, day) / MS_PER_DAY);
}

/** Tagesnummer → 'YYYY-MM-DD' (für die Erwartungs-Datumsliste der Deckungsprüfung). */
function dayNumberToIsoDate(dayNumber: number): string {
  return new Date(dayNumber * MS_PER_DAY).toISOString().slice(0, 10);
}

// ─── §13.10 Schreibgrenze: countryCode-/regionKey-Format je Tag ───────────────

/**
 * Prüft je Tag zwei zusätzliche Schreibgrenzen (architecture-reisekosten.md §13.10, Reviewer
 * F1 — bisher ungeprüft): `countryCode` MUSS `^[A-Z]{2}$` (ISO-3166-1 alpha-2) entsprechen, und
 * `regionKey` MUSS — falls gesetzt — ein non-empty String sein (nicht leer/nur Whitespace).
 * Additive Defense-in-Depth-Schicht: der Rechenkern (`resolveForeignPerDiemRate`) läuft bei
 * einem unbekannten `countryCode` ohnehin fail-closed in `noRatesForDate`/
 * `foreignCountryNotSupported` — diese Prüfung verhindert zusätzlich, dass strukturell
 * unplausible Werte (Kleinschreibung, Zahlen, leerer `regionKey`) überhaupt persistiert werden.
 */
function validateDayCountryAndRegion(days: TravelDayRecord[]): TravelValidationIssue[] {
  const issues: TravelValidationIssue[] = [];
  for (const day of days) {
    if (!COUNTRY_CODE_RE.test(day.countryCode)) {
      issues.push({
        code: 'invalidCountryCode',
        detail:
          `days[${day.dateISO}].countryCode ist kein gültiger ISO-3166-1-alpha-2-Code ` +
          `(erhalten "${day.countryCode}", erwartet z.B. "DE").`,
      });
    }
    if (day.regionKey !== undefined && day.regionKey.trim().length === 0) {
      issues.push({
        code: 'emptyRegionKey',
        detail: `days[${day.dateISO}].regionKey darf, wenn gesetzt, nicht leer/whitespace sein.`,
      });
    }
  }
  return issues;
}

// ─── days[]-Deckungsprüfung ────────────────────────────────────────────────────

/**
 * Prüft, dass `days[]` GENAU die Kalendertage von `departureDateISO` bis `returnDateISO`
 * lückenlos und aufsteigend (je Tag genau einmal) abdeckt, UND dass der letzte Reisetag
 * `overnight === false` trägt (§4.1 — der Kern ignoriert den Wert des letzten Tages, die
 * Schreibgrenze erzwingt ihn dennoch als `false`, um widersprüchliche Datensätze gar nicht
 * erst persistieren zu lassen).
 *
 * @returns Leeres Array bei Erfolg, sonst eine oder mehrere {@link TravelValidationIssue}s.
 */
export function validateDaysCoverage(
  departureDateISO: string,
  returnDateISO: string,
  days: TravelDayRecord[],
): TravelValidationIssue[] {
  const issues: TravelValidationIssue[] = [];
  if (!isValidIsoDate(departureDateISO)) {
    issues.push({
      code: 'invalidDepartureDate',
      detail: `departureDateISO ist kein gültiges Kalenderdatum (erhalten "${departureDateISO}").`,
    });
  }
  if (!isValidIsoDate(returnDateISO)) {
    issues.push({
      code: 'invalidReturnDate',
      detail: `returnDateISO ist kein gültiges Kalenderdatum (erhalten "${returnDateISO}").`,
    });
  }
  if (issues.length > 0) {
    // Ohne gültige Rahmendaten kann die Deckung nicht sinnvoll geprüft werden.
    return issues;
  }

  const departureDayNumber = isoDateToDayNumber(departureDateISO);
  const returnDayNumber = isoDateToDayNumber(returnDateISO);
  if (returnDayNumber < departureDayNumber) {
    issues.push({ code: 'returnBeforeDeparture', detail: 'returnDateISO liegt vor departureDateISO.' });
    return issues;
  }

  const expectedDates: string[] = [];
  for (let offset = 0; offset <= returnDayNumber - departureDayNumber; offset += 1) {
    expectedDates.push(dayNumberToIsoDate(departureDayNumber + offset));
  }

  if (days.length !== expectedDates.length) {
    issues.push({
      code: 'daysCountMismatch',
      detail:
        `days[] hat ${days.length} Einträge, erwartet werden ${expectedDates.length} ` +
        '(lückenlos departure..return).',
    });
    return issues;
  }

  const sortedDays = [...days].sort((a, b) => (a.dateISO < b.dateISO ? -1 : a.dateISO > b.dateISO ? 1 : 0));
  for (let index = 0; index < expectedDates.length; index += 1) {
    if (sortedDays[index].dateISO !== expectedDates[index]) {
      issues.push({
        code: 'daysCoverageMismatch',
        detail:
          'days[] deckt die Kalendertage nicht lückenlos/eindeutig ab ' +
          `(Lücke oder Duplikat bei erwartetem Datum ${expectedDates[index]}).`,
      });
      return issues;
    }
  }

  const lastDay = sortedDays[sortedDays.length - 1];
  if (lastDay.overnight !== false) {
    issues.push({
      code: 'lastDayOvernightMustBeFalse',
      detail:
        `overnight des letzten Reisetags (${lastDay.dateISO}) muss false sein — ` +
        'die Reise endet an diesem Tag, danach findet keine Übernachtung mehr statt.',
    });
  }

  // NEU (§13.10, additiv): Format-Schreibgrenze je Tag — countryCode/regionKey.
  issues.push(...validateDayCountryAndRegion(sortedDays));

  return issues;
}

// ─── mileage-Validierung ───────────────────────────────────────────────────────

/**
 * Prüft ein optionales Adressfeld (`originAddress`/`destinationAddress`, RK-19, §14.3):
 * `undefined` ist immer gültig (Feld nicht gesetzt); gesetzt aber (nach Trim) leer ⇒
 * `addressEmpty`; gesetzt und länger als 300 Zeichen (nach Trim) ⇒ `addressTooLong`.
 */
function validateAddressField(value: string | undefined, fieldName: 'originAddress' | 'destinationAddress'): TravelValidationIssue[] {
  if (value === undefined) {
    return [];
  }
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return [
      {
        code: 'addressEmpty',
        detail: `mileage.${fieldName} darf, wenn gesetzt, nicht leer/whitespace sein.`,
      },
    ];
  }
  if (trimmed.length > 300) {
    return [
      {
        code: 'addressTooLong',
        detail: `mileage.${fieldName} darf höchstens 300 Zeichen haben (erhalten ${trimmed.length}).`,
      },
    ];
  }
  return [];
}

/**
 * Validiert eine {@link TravelMileage}-Angabe (§4.1, Δ RK-19 §14.3): `km > 0`, endlich,
 * höchstens eine Dezimalstelle; die NEUEN optionalen Route-Lookup-Felder
 * (`originAddress`/`destinationAddress`/`suggestedKm`/`kmSource`) inkl. Konsistenzregeln; sowie
 * die `routeNote`-Pflicht (Eigenbeleg-Angabe, §8.2) nach der E16-Ausnahme-Regel (rev.5/A1).
 * `null` ist immer gültig (keine eigene Fahrt).
 */
export function validateMileage(mileage: TravelMileage | null): TravelValidationIssue[] {
  if (mileage === null) {
    return [];
  }
  const issues: TravelValidationIssue[] = [];

  if (!Number.isFinite(mileage.km)) {
    issues.push({ code: 'kmNotFinite', detail: `mileage.km muss eine endliche Zahl sein (erhalten ${mileage.km}).` });
  } else if (mileage.km <= 0) {
    issues.push({ code: 'kmNotPositive', detail: `mileage.km muss > 0 sein (erhalten ${mileage.km}).` });
  } else {
    const roundedToOneDecimal = Math.round(mileage.km * 10) / 10;
    if (Math.abs(roundedToOneDecimal - mileage.km) > 1e-9) {
      issues.push({
        code: 'kmTooManyDecimals',
        detail: `mileage.km darf höchstens eine Dezimalstelle haben (erhalten ${mileage.km}).`,
      });
    }
  }

  // NEU (RK-19, §14.3): Adress-Format je Feld (falls gesetzt).
  issues.push(...validateAddressField(mileage.originAddress, 'originAddress'));
  issues.push(...validateAddressField(mileage.destinationAddress, 'destinationAddress'));

  // NEU (RK-19, §14.3): suggestedKm — falls gesetzt: endlich, > 0, höchstens 1 Dezimalstelle.
  if (mileage.suggestedKm !== undefined) {
    if (!Number.isFinite(mileage.suggestedKm) || mileage.suggestedKm <= 0) {
      issues.push({
        code: 'suggestedKmInvalid',
        detail: `mileage.suggestedKm muss eine endliche Zahl > 0 sein (erhalten ${mileage.suggestedKm}).`,
      });
    } else {
      const roundedSuggestedKm = Math.round(mileage.suggestedKm * 10) / 10;
      if (Math.abs(roundedSuggestedKm - mileage.suggestedKm) > 1e-9) {
        issues.push({
          code: 'suggestedKmInvalid',
          detail: `mileage.suggestedKm darf höchstens eine Dezimalstelle haben (erhalten ${mileage.suggestedKm}).`,
        });
      }
    }
  }

  // NEU (RK-19, §14.3): kmSource ∈ {'route_suggested','route_adjusted'} verlangt konsistent
  // gesetzte suggestedKm UND beide Adressen — sonst kmSourceInconsistent (Soll-Paar #48).
  if (mileage.kmSource === 'route_suggested' || mileage.kmSource === 'route_adjusted') {
    const hasSuggestedKm = mileage.suggestedKm !== undefined;
    const hasBothAddresses = mileage.originAddress !== undefined && mileage.destinationAddress !== undefined;
    if (!hasSuggestedKm || !hasBothAddresses) {
      issues.push({
        code: 'kmSourceInconsistent',
        detail:
          `mileage.kmSource="${mileage.kmSource}" verlangt gesetzte suggestedKm UND beide Adressen ` +
          `(erhalten suggestedKm=${String(mileage.suggestedKm)}, originAddress=${String(mileage.originAddress)}, ` +
          `destinationAddress=${String(mileage.destinationAddress)}).`,
      });
    }
  }

  // routeNote-Pflicht (E16, rev.5/A1): erst Adress-Ausnahme, dann Umweg-Rücknahme prüfen.
  if (mileage.routeNote.trim().length === 0) {
    const originNonEmpty = mileage.originAddress !== undefined && mileage.originAddress.trim().length > 0;
    const destinationNonEmpty = mileage.destinationAddress !== undefined && mileage.destinationAddress.trim().length > 0;
    const bothAddressesSet = originNonEmpty && destinationNonEmpty;

    if (!bothAddressesSet) {
      issues.push({
        code: 'routeNoteRequired',
        detail: 'mileage.routeNote darf nicht leer sein (Eigenbeleg-Angabe, §8.2 Recht-Recherche).',
      });
    } else {
      const isDetour =
        mileage.kmSource === 'route_adjusted' &&
        mileage.suggestedKm !== undefined &&
        mileage.km > mileage.suggestedKm;
      if (isDetour) {
        issues.push({
          code: 'routeNoteRequiredDetour',
          detail:
            'mileage.routeNote darf nicht leer sein — Umweg über den ermittelten Vorschlag hinaus ' +
            `(km=${mileage.km} > suggestedKm=${mileage.suggestedKm}) erfordert eine Eigenbeleg-Begründung (A1/rev.5).`,
        });
      }
    }
  }

  return issues;
}

// ─── HH:mm-Zeitfeld-Validierung ────────────────────────────────────────────────

/** Validiert `departureTime`/`returnTime` als 'HH:mm'. */
function validateTimeFields(departureTime: string, returnTime: string): TravelValidationIssue[] {
  const issues: TravelValidationIssue[] = [];
  if (!isValidHhMm(departureTime)) {
    issues.push({
      code: 'invalidDepartureTime',
      detail: `departureTime ist kein gültiges HH:mm-Format (erhalten "${departureTime}").`,
    });
  }
  if (!isValidHhMm(returnTime)) {
    issues.push({
      code: 'invalidReturnTime',
      detail: `returnTime ist kein gültiges HH:mm-Format (erhalten "${returnTime}").`,
    });
  }
  return issues;
}

// ─── Struktur-Validierung: Create ──────────────────────────────────────────────

/**
 * Validiert die Eingabe von {@link createTravelTrip} (`travelApi.ts`): HH:mm-Formate,
 * lückenlose `days[]`-Deckung inkl. `overnight`-Regel des letzten Tages, sowie `mileage`
 * (falls gesetzt). `purpose`/`destinationCity` sind hier NICHT Pflicht — sie werden erst
 * beim Settle erzwungen (§4.1: „Pflicht non-empty SPÄTESTENS beim Settle").
 */
export function validateCreateTravelTripInput(input: CreateTravelTripInput): ValidationResult {
  const issues: TravelValidationIssue[] = [
    ...validateTimeFields(input.departureTime, input.returnTime),
    ...validateDaysCoverage(input.departureDateISO, input.returnDateISO, input.days),
    ...validateMileage(input.mileage),
  ];
  return issues.length === 0 ? ok() : fail(issues);
}

// ─── Struktur-Validierung: Update (Partial) ────────────────────────────────────

/**
 * Validiert einen {@link UpdateTravelTripPatch} (`travelApi.ts`). Da der Patch partiell ist,
 * werden nur die tatsächlich enthaltenen Felder geprüft: HH:mm-Formate (falls vorhanden),
 * `mileage` (falls vorhanden, inkl. explizitem `null`-Reset), sowie die volle
 * `days[]`-Deckungsprüfung NUR, wenn `days` UND beide Rahmendaten (`departureDateISO`/
 * `returnDateISO`) gemeinsam im selben Patch enthalten sind (sonst fehlt ein verlässlicher
 * Vergleichsrahmen — die vollständige Deckung wird spätestens beim Settle gegen den
 * gespeicherten Gesamt-Datensatz geprüft, siehe {@link validateTripForSettle}).
 */
export function validateUpdateTravelTripPatch(patch: UpdateTravelTripPatch): ValidationResult {
  const issues: TravelValidationIssue[] = [];

  if (patch.departureTime !== undefined && !isValidHhMm(patch.departureTime)) {
    issues.push({
      code: 'invalidDepartureTime',
      detail: `departureTime ist kein gültiges HH:mm-Format (erhalten "${patch.departureTime}").`,
    });
  }
  if (patch.returnTime !== undefined && !isValidHhMm(patch.returnTime)) {
    issues.push({
      code: 'invalidReturnTime',
      detail: `returnTime ist kein gültiges HH:mm-Format (erhalten "${patch.returnTime}").`,
    });
  }
  if (patch.mileage !== undefined) {
    issues.push(...validateMileage(patch.mileage));
  }
  if (patch.days !== undefined && patch.departureDateISO !== undefined && patch.returnDateISO !== undefined) {
    issues.push(...validateDaysCoverage(patch.departureDateISO, patch.returnDateISO, patch.days));
  }

  return issues.length === 0 ? ok() : fail(issues);
}

// ─── Struktur-Validierung: Settle (voller, gespeicherter Trip) ────────────────

/**
 * Validiert einen vollständigen, geladenen {@link TravelTrip} vor `settleTravelTrip`
 * (§3.3 Pflichten a–d): non-empty `purpose`/`destinationCity`, HH:mm-Formate, lückenlose
 * `days[]`-Deckung inkl. `overnight`-Regel des letzten Tages, sowie `mileage`.
 *
 * Diese Prüfung läuft VOR dem Aufruf von `calculateTravelAllowances` — sie fängt strukturell
 * fehlerhafte Datensätze (z.B. leeren Anlass) ab, die der Berechnungskern selbst gar nicht
 * kennt (der Kern prüft nur rechen-relevante Felder, nicht `purpose`/`destinationCity`).
 */
export function validateTripForSettle(trip: TravelTrip): ValidationResult {
  const issues: TravelValidationIssue[] = [];

  if (trip.purpose.trim().length === 0) {
    issues.push({ code: 'purposeRequired', detail: 'purpose darf beim Abrechnen nicht leer sein (§8.1 GoBD).' });
  }
  if (trip.destinationCity.trim().length === 0) {
    issues.push({
      code: 'destinationCityRequired',
      detail: 'destinationCity darf beim Abrechnen nicht leer sein.',
    });
  }

  issues.push(...validateTimeFields(trip.departureTime, trip.returnTime));
  issues.push(...validateDaysCoverage(trip.departureDateISO, trip.returnDateISO, trip.days));
  issues.push(...validateMileage(trip.mileage));

  return issues.length === 0 ? ok() : fail(issues);
}

// ─── Integer-Cent-/Number.isFinite-Prüfung des Settlement-Snapshots (§3.3c PFLICHT) ─

/** Ergebnis von {@link validateCalcSnapshotCents} — bewusst KEIN Issue-Code-Vokabular (interner Defense-in-Depth-Guard, nicht Teil des Fehler-Code-Vertrags §14.3). */
export interface CentSnapshotValidationResult {
  valid: boolean;
  /** Menschenlesbare technische Fehlermeldungen (leer, wenn `valid === true`) — NUR für logger/`TravelApiError.message`. */
  errors: string[];
}

/**
 * Prüft den KOMPLETTEN `ok:true`-Snapshot eines {@link TravelCalcResult} auf
 * `Number.isFinite` UND `Number.isInteger` je Cent-Feld — Tages- UND Summenebene.
 *
 * PFLICHT, nicht optional (§3.3c, Reviewer F1): `settleTravelTrip` MUSS diese Prüfung VOR
 * dem Firestore-Write ausführen, ZUSÄTZLICH zum kern-internen Abschluss-Guard
 * (`travelAllowance.ts` Regel 8). Bei einem korrekt implementierten Kern ist diese Prüfung
 * unerreichbar (Defense-in-Depth, V12) — sie existiert, damit ein `NaN` strukturell NIE
 * einen `settlement`-Snapshot in Firestore erreichen kann, selbst wenn der Kern künftig
 * fehlerhaft verändert würde.
 *
 * Bewusst NICHT auf {@link TravelValidationIssue}/{@link ValidationResult} umgestellt (RK-18):
 * dieser interne Guard wird im UI nie feldgranular angezeigt — `travelApi.ts` mappt ein
 * Fehlschlagen pauschal auf `TravelApiError('snapshotInvalid', …)` (§14.8), die `errors[]`
 * landen nur in der technischen `message` (Logs/Tests).
 */
export function validateCalcSnapshotCents(calc: Extract<TravelCalcResult, { ok: true }>): CentSnapshotValidationResult {
  const errors: string[] = [];
  const isFiniteInt = (value: number): boolean => Number.isFinite(value) && Number.isInteger(value);

  for (const day of calc.days) {
    if (!isFiniteInt(day.baseCents)) {
      errors.push(`days[${day.dateISO}].baseCents ist kein finiter Integer (${day.baseCents}).`);
    }
    if (!isFiniteInt(day.reductionBreakfastCents)) {
      errors.push(`days[${day.dateISO}].reductionBreakfastCents ist kein finiter Integer (${day.reductionBreakfastCents}).`);
    }
    if (!isFiniteInt(day.reductionLunchCents)) {
      errors.push(`days[${day.dateISO}].reductionLunchCents ist kein finiter Integer (${day.reductionLunchCents}).`);
    }
    if (!isFiniteInt(day.reductionDinnerCents)) {
      errors.push(`days[${day.dateISO}].reductionDinnerCents ist kein finiter Integer (${day.reductionDinnerCents}).`);
    }
    if (!isFiniteInt(day.allowanceCents)) {
      errors.push(`days[${day.dateISO}].allowanceCents ist kein finiter Integer (${day.allowanceCents}).`);
    }
  }

  if (!isFiniteInt(calc.verpflegungCents)) {
    errors.push(`verpflegungCents ist kein finiter Integer (${calc.verpflegungCents}).`);
  }
  if (!isFiniteInt(calc.mileageCents)) {
    errors.push(`mileageCents ist kein finiter Integer (${calc.mileageCents}).`);
  }
  if (!isFiniteInt(calc.totalCents)) {
    errors.push(`totalCents ist kein finiter Integer (${calc.totalCents}).`);
  }

  return { valid: errors.length === 0, errors };
}
